import './multistep.scss';

import enquire from 'enquire.js';
import Splide from '@splidejs/splide';
import { addScrollAnimation } from '../utils/scrollAnimation';
import { Responsive, Translations } from '../utils';

const COMPONENT_NAME = 'tcl-multistep';

const BEM = {
  BLOCK: COMPONENT_NAME,
};

/**
 * version    1.0
 * class      <type> (multistep)
 * classdesc  Multistep
 */
export class Multistep {
  constructor() {
    this._currentStep = 0;
    this._scrollCleanup = null;

    this._findUIElements();

    if (!this._els.multistep.length) return;
    this._totalSteps = Number(this._els.multistep.data('total-steps')) || 4;

    this._initSplide();

    enquire.register(`${Responsive.getBreakpointMaxMediaQuery('md')}`, () => {
      this._splide.on('mounted', () => {
        this._setupMobileAnimation();
      });
    });

    this._bindEvents();
    this._updateUI();

    enquire.register(`${Responsive.getBreakpointMaxMediaQuery('md')}`, () => {
      this._setupMobileAnimation();
    });
  }

  // private methods

  _findUIElements() {
    this._els = {
      multistep: $(`.${BEM.BLOCK}`),
      slider: $(`.${BEM.BLOCK}__slider`),
      next: $(`.${BEM.BLOCK}__next`),
      prev: $(`.${BEM.BLOCK}__prev`),
      pagination: $(`.${BEM.BLOCK}__pagination`),
      restart: $(`.${BEM.BLOCK}__restart`),
      footer: $(`.${BEM.BLOCK}-footer`),
    };
  }

  _setupMobileAnimation() {
    if (this._scrollCleanup) {
      this._scrollCleanup();
      this._scrollCleanup = null;
    }

    if (Responsive.matchBreakpoint(`${Responsive.getBreakpointMinMediaQuery('lg')}`)) {
      return;
    }

    const img = this._els.slider.find('.splide__slide.is-active .mobile-img-wrapper')[0];

    if (img) {
      this._scrollCleanup = addScrollAnimation(img, {
        offset: -350,
        range: 200,
        minWidthPercent: 94,
        maxRadius: 8,
      });
    }
  }

  _initSplide() {
    this._splide = new Splide(this._els.slider[0], {
      arrows: false,
      pagination: false,
      drag: false,
      keyboard: true,
      accessibility: true,
      type: 'fade',
      speed: 50,
      i18n: Translations.getSplideI18n(),
    });

    this._splide.mount();

    this._splide.on('moved', (index) => {
      this._currentStep = index;
      this._updateUI();
      this._setupMobileAnimation();
    });
  }

  _bindEvents() {
    this._els.multistep.on('change', 'input[type="radio"]', () => {
      this._updateUI();
    });

    this._els.next.on('click', () => {
      if (this._canGoNext()) {
        this._splide.go('>');
      }
    });

    this._els.prev.on('click', () => {
      if (this._currentStep > 0) {
        this._splide.go('<');
      }
    });

    this._els.restart.on('click', () => {
      this._reset();
    });
  }

  _canGoNext() {
    if (this._currentStep >= this._totalSteps) return false;

    const slide = this._getCurrentSlide();
    return slide.find('input[type="radio"]:checked').length > 0;
  }

  _getCurrentSlide() {
    return $(this._splide.Components.Elements.slides[this._currentStep]);
  }

  _updateUI() {
    const isFirst = this._currentStep === 0;
    const isFinal = this._currentStep === this._totalSteps;

    this._els.prev.prop('disabled', isFirst).attr('aria-disabled', isFirst);

    const canNext = this._canGoNext();
    this._els.next.toggle(!isFinal).prop('disabled', !canNext).attr('aria-disabled', !canNext);

    this._renderPagination(!isFinal);

    this._els.restart.toggle(isFinal);

    enquire.register(`${Responsive.getBreakpointMinMediaQuery('sm')}`, () => {
      this._updateSliderBackground();
    });
  }

  _renderPagination(visible) {
    this._els.pagination.empty();

    if (!visible) {
      this._els.footer.hide();
      return;
    }

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < this._totalSteps; i++) {
      const isActive = i === this._currentStep;

      $('<button>', {
        class: `btn btn-sm ${isActive ? 'btn-primary' : 'btn-outline'}`,
        'aria-label': Translations.getLabel('MULTISTEP_STEP_ARIA_LABEL', { step: i + 1 }, `Step ${i + 1}`),
        'aria-current': isActive ? 'step' : false,
        text: i + 1,
        click: () => this._splide.go(i),
      }).appendTo(this._els.pagination);
    }
  }

  _reset() {
    this._els.multistep.find('input[type="radio"]').prop('checked', false);

    this._splide.go(0);
    this._currentStep = 0;
    this._els.footer.show();
    this._updateUI();
  }

  _updateSliderBackground() {
    const $currentSlide = this._getCurrentSlide();
    const imgSrc = $currentSlide.attr('data-src');

    this._els.slider.find('.splide__track').css({
      backgroundImage: `url(${imgSrc})`,
      backgroundSize: 'cover',
      backgroundPosition: 'left',
      backgroundRepeat: 'no-repeat',
    });
  }
}
